import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Upload,
  FileSpreadsheet,
  Send,
  AlertCircle,
  CheckCircle,
  X,
  Download,
  Check,
  Loader2,
  Lock,
  Sparkles,
} from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthStore } from "@/store/auth.store";
import { generateUUID } from "@/utils/transform";
import { bulkSendApi, templateApi } from "../../services/api";

const SAMPLE_CSV = `email,name,company,role
john@example.com,John Smith,Acme Corp,Manager
jane@example.com,Jane Doe,Tech Inc,Director
bob@example.com,Bob Wilson,StartupXYZ,CEO`;

// ============================================
// UpgradeRequired Component
// ============================================
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("bulkSend.title", "Bulk Send")}
        </h1>
        <p className="text-muted-foreground">
          {t(
            "bulkSend.subtitle",
            "Send documents to multiple recipients at once",
          )}
        </p>
      </div>

      {/* Single Card with divider */}
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.bulkSendUpgradeDesc",
              "Bulk Send allows you to send the same document to hundreds of recipients at once using a CSV file.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="mr-2 h-4 w-4" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">
              {t("bulkSend.withBulkSendYouCan", "With Bulk Send you can:")}
            </h3>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-center gap-2">
                <Send className="h-4 w-4 text-primary" />
                {t(
                  "bulkSend.feature1",
                  "Send to hundreds of recipients at once",
                )}
              </li>
              <li className="flex items-center gap-2">
                <Send className="h-4 w-4 text-primary" />
                {t(
                  "bulkSend.feature2",
                  "Personalize each document with recipient data",
                )}
              </li>
              <li className="flex items-center gap-2">
                <Send className="h-4 w-4 text-primary" />
                {t("bulkSend.feature3", "Import recipients from CSV files")}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const BulkSend = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get user subscription to check feature access
  const user = useAuthStore((state) => state.user);

  // Check if user has bulk send feature - must be explicitly true
  const hasBulkSendFeature = user?.subscription?.features?.bulkSend === true;

  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [bulkSendName, setBulkSendName] = useState("");
  const [error, setError] = useState(null);
  const [createdBulkSend, setCreatedBulkSend] = useState(null);

  const steps = [
    { num: 1, label: t("bulkSend.steps.selectTemplate") },
    { num: 2, label: t("bulkSend.steps.uploadRecipients") },
    { num: 3, label: t("bulkSend.steps.mapFields") },
    { num: 4, label: t("bulkSend.steps.reviewSend") },
  ];

  // Query: Load templates (only if feature is enabled)
  const { data: templates = [] } = useQuery({
    queryKey: ["templates", "bulk-send"],
    queryFn: async () => {
      const templatesRes = await templateApi.list();
      return templatesRes.data?.data || [];
    },
    staleTime: 30000,
    enabled: hasBulkSendFeature, // Don't fetch if feature is disabled
  });

  // Mutation: Create bulk send
  const createBulkSendMutation = useMutation({
    mutationFn: async (data) => {
      const response = await bulkSendApi.create(data);
      return response.data?.data;
    },
    onSuccess: (data) => {
      setCreatedBulkSend(data);
      setStep(4);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      setError(errorData?.message || "Failed to create bulk send");
    },
  });

  // Mutation: Start bulk send
  const startBulkSendMutation = useMutation({
    mutationFn: (bulkSendId) => bulkSendApi.start(bulkSendId),
    onSuccess: () => {
      navigate("/envelopes");
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to start bulk send");
    },
  });

  // Combined loading state
  const loading =
    createBulkSendMutation.isPending || startBulkSendMutation.isPending;

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setCsvData(text);

      // Parse CSV for preview
      const lines = text.split("\n").filter((line) => line.trim());
      const headers = lines[0].split(",").map((h) => h.trim());
      const rows = lines.slice(1, 6).map((line) => {
        const values = line.split(",").map((v) => v.trim());
        return headers.reduce((obj, header, i) => {
          obj[header] = values[i] || "";
          return obj;
        }, {});
      });

      setCsvPreview({ headers, rows, totalRows: lines.length - 1 });

      // Auto-map common fields
      const autoMapping = {};
      headers.forEach((header) => {
        const lower = header.toLowerCase();
        if (lower.includes("email")) autoMapping.email = header;
        if (lower.includes("name") && !lower.includes("company")) {
          autoMapping.name = header;
        }
      });
      setFieldMapping(autoMapping);
    };
    reader.readAsText(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".csv"],
    },
    maxFiles: 1,
  });

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-bulk-send.csv";
    a.click();
  };

  const handleCreate = () => {
    if (!selectedTemplate || !csvData || !fieldMapping.email) {
      setError(t("errors.completeRequiredFields"));
      return;
    }

    setError(null);
    createBulkSendMutation.mutate({
      name: bulkSendName || `Bulk Send - ${new Date().toLocaleDateString()}`,
      templateId: selectedTemplate.id,
      csvData,
      fieldMapping,
    });
  };

  const handleStart = () => {
    if (!createdBulkSend) return;
    startBulkSendMutation.mutate(createdBulkSend.id);
  };

  const renderStepsClass = (s) => {
    if (step > s?.num) {
      return "bg-primary text-primary-foreground";
    }
    return step === s?.num
      ? "bg-primary text-primary-foreground"
      : "bg-muted text-muted-foreground";
  };

  // ============================================
  // Feature Check - Show upgrade message if bulk send not available
  // ============================================
  if (!hasBulkSendFeature) {
    return <UpgradeRequired />;
  }

  const renderStepContent = () => {
    switch (step) {
      case 1: {
        // Filter templates that are ready for bulk send
        const readyTemplates = templates.filter(
          (t) => t.documentStoragePath && (t.fieldCount || 0) > 0,
        );
        const notReadyTemplates = templates.filter(
          (t) => !t.documentStoragePath || (t.fieldCount || 0) === 0,
        );

        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Select a Template
              </h2>
              <p className="text-sm text-muted-foreground">
                Select a template with an uploaded document and signature
                fields.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">{t("common.template")}</Label>
              <Select
                value={selectedTemplate?.id || ""}
                onValueChange={(value) => {
                  const template = templates.find((t) => t.id === value);
                  setSelectedTemplate(template);
                }}
              >
                <SelectTrigger className="w-full bg-background">
                  <SelectValue placeholder={t("placeholders.selectTemplate")} />
                </SelectTrigger>
                <SelectContent>
                  {readyTemplates.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        Ready for Bulk Send
                      </div>
                      {readyTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                            <span>{template.name}</span>
                            <span className="text-muted-foreground text-xs">
                              ({template.fieldCount || 0} fields)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {notReadyTemplates.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                        Not Ready
                      </div>
                      {notReadyTemplates.map((template) => {
                        const hasDocument = !!template.documentStoragePath;
                        const hasFields = (template.fieldCount || 0) > 0;
                        let status = "";
                        if (!hasDocument) status = t("templates.noDocument");
                        else if (!hasFields) status = t("templates.noFields");

                        return (
                          <SelectItem
                            key={template.id}
                            value={template.id}
                            disabled
                          >
                            <div className="flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">
                                {template.name}
                              </span>
                              <span className="text-muted-foreground text-xs">
                                ({status})
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-8 h-8 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">
                      {selectedTemplate.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {selectedTemplate.fieldCount || 0} field(s) • Ready for
                      bulk send
                    </p>
                  </div>
                </div>
              </div>
            )}

            {templates.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t("templates.noTemplatesFound")}</p>
              </div>
            )}

            {templates.length > 0 && readyTemplates.length === 0 && (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Note:</strong> None of your templates are ready for
                  bulk send. Please edit a template to upload a document and add
                  signature fields.
                </p>
              </div>
            )}
          </div>
        );
      }

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Upload Recipients
              </h2>
              <p className="text-sm text-muted-foreground">
                Upload a CSV file with recipient information.
              </p>
            </div>

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              {isDragActive ? (
                <p className="text-primary">{t("common.dropCsvHere")}</p>
              ) : (
                <>
                  <p className="text-foreground">
                    Drag & drop a CSV file here, or click to browse
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Supported format: .csv
                  </p>
                </>
              )}
            </div>

            <button
              onClick={downloadSample}
              className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
            >
              <Download className="w-4 h-4" />
              Download sample CSV
            </button>

            {csvPreview.headers && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-foreground">
                    Preview ({csvPreview.totalRows} recipients)
                  </h3>
                  <button
                    onClick={() => {
                      setCsvData(null);
                      setCsvPreview([]);
                    }}
                    className="text-sm text-destructive hover:text-destructive/80"
                  >
                    Remove
                  </button>
                </div>
                <div className="overflow-x-auto border border-border rounded-lg">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted">
                      <tr>
                        {csvPreview.headers.map((header) => (
                          <th
                            key={header}
                            className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-card">
                      {csvPreview.rows.map((row) => (
                        <tr key={generateUUID()}>
                          {csvPreview.headers.map((header) => (
                            <td
                              key={header}
                              className="px-4 py-2 text-sm text-foreground"
                            >
                              {row[header]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {csvPreview.totalRows > 5 && (
                  <p className="text-sm text-muted-foreground">
                    Showing 5 of {csvPreview.totalRows} recipients
                  </p>
                )}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Map CSV Fields
              </h2>
              <p className="text-sm text-muted-foreground">
                Match your CSV columns to the required fields.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground">
                  Email Column <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={fieldMapping.email || ""}
                  onValueChange={(value) =>
                    setFieldMapping({ ...fieldMapping, email: value })
                  }
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder={t("placeholders.selectColumn")} />
                  </SelectTrigger>
                  <SelectContent>
                    {csvPreview.headers?.map((header) => (
                      <SelectItem key={header} value={header}>
                        {header}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">
                  {t("common.nameColumn")}
                </Label>
                <Select
                  value={fieldMapping.name || ""}
                  onValueChange={(value) =>
                    setFieldMapping({ ...fieldMapping, name: value })
                  }
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder={t("placeholders.selectColumn")} />
                  </SelectTrigger>
                  <SelectContent>
                    {csvPreview.headers?.map((header) => (
                      <SelectItem key={header} value={header}>
                        {header}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">{t("bulkSend.name")}</Label>
                <Input
                  type="text"
                  value={bulkSendName}
                  onChange={(e) => setBulkSendName(e.target.value)}
                  placeholder={`Bulk Send - ${new Date().toLocaleDateString()}`}
                  className="bg-background"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                Bulk Send Created
              </h2>
              <p className="text-muted-foreground">
                Ready to send to {createdBulkSend?.totalItems} recipients
              </p>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("common.template")}
                  </p>
                  <p className="font-medium text-foreground">
                    {selectedTemplate?.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("common.recipients")}
                  </p>
                  <p className="font-medium text-foreground">
                    {createdBulkSend?.totalItems}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => navigate("/envelopes")}>
                Send Later
              </Button>
              <Button onClick={handleStart} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Now
                  </>
                )}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight text-foreground">
            Bulk Send
          </h1>
          <p className="text-muted-foreground">
            Send documents to multiple recipients at once
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <nav className="mb-8">
        <ol className="flex items-center">
          {steps.map((s, index) => (
            <li
              key={s.num}
              className={`flex items-center ${
                index < steps.length - 1 ? "flex-1" : ""
              }`}
            >
              <div className="flex items-center">
                <span
                  className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium ${renderStepsClass(s)}`}
                >
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </span>
                <span
                  className={`ml-2 text-sm font-medium hidden sm:inline ${
                    step >= s.num ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-4 ${
                    step > s.num ? "bg-primary" : "bg-muted"
                  }`}
                />
              )}
            </li>
          ))}
        </ol>
      </nav>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-destructive">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto hover:bg-destructive/20 rounded p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Step Content */}
      <Card className="p-6">{renderStepContent()}</Card>

      {/* Navigation Buttons */}
      {step < 4 && (
        <div className="flex justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate("/templates")}>
              Cancel
            </Button>
            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 1 && !selectedTemplate) || (step === 2 && !csvData)
                }
              >
                Continue
              </Button>
            ) : (
              step === 3 && (
                <Button
                  onClick={handleCreate}
                  disabled={loading || !fieldMapping.email}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    t("bulkSend.createBulkSend")
                  )}
                </Button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkSend;
